import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import { isEmpty } from 'lodash'
import { IconDots, IconX } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { stopListeningToCall } from '@/reducers/commandCenter/commandCenter.redux'
import { stopListeningToAlert } from '@/reducers/realtime/realtime.actions'
import {
  SET_ACTIVE_USERNAME,
  END_CHAT,
  selectOpenChats,
} from '@/reducers/realtime/realtimeChats.redux'

/**
 * "All chats" menu in the Agent Assist header — every open conversation, with
 * its unread count and its own End chat. Opens on hover so a supervisor can
 * glance at the list mid-call, and on click or focus because hover doesn't
 * exist on a keyboard or a touch screen. It closes when the pointer leaves, on
 * Escape, or once a row is chosen.
 */
export const AssistChatsMenu = () => {
  const dispatch = useDispatch()
  const [menuOpen, setMenuOpen] = useState(false)
  const { chats = {}, activeUsername = null } = useSelector((state) => state.realtimeChats) || {}
  const { listeningToUsername } = useSelector((state) => state.realtimeUserAlerts)
  const { listeningToAgent } = useSelector((state) => state.commandCenter)

  const openChats = selectOpenChats(chats).sort(
    (first, second) => first.raisedSeq - second.raisedSeq
  )

  if (isEmpty(openChats)) {
    return null
  }

  // The manager is done helping this agent: drop the thread, and don't leave a
  // listen running against a chat that no longer exists.
  const endChat = (agentUsername) => {
    if (listeningToUsername === agentUsername || listeningToAgent === agentUsername) {
      dispatch(stopListeningToAlert())
      dispatch(stopListeningToCall())
    }
    dispatch({ type: END_CHAT, username: agentUsername })
  }

  const chatList = (
    <div className="assist-chats-menu" data-testid="assist-chats-menu">
      {openChats.map((chat) => (
        <div
          key={chat.agentUsername}
          className={classNames('assist-chats-menu__row', {
            'assist-chats-menu__row--active': chat.agentUsername === activeUsername,
          })}
        >
          <button
            type="button"
            className="assist-chats-menu__name"
            data-testid={`assist-chats-menu-select-${chat.agentUsername}`}
            onClick={() => {
              dispatch({ type: SET_ACTIVE_USERNAME, activeUsername: chat.agentUsername })
              setMenuOpen(false)
            }}
          >
            {`${chat.agentFirstName} ${chat.agentLastName}`}
          </button>
          {chat.unread > 0 && <span className="assist-chats-menu__unread">{chat.unread}</span>}
          <Button
            small
            data-testid={`close-chat-${chat.agentUsername}`}
            onClick={() => endChat(chat.agentUsername)}
          >
            <IconX />
            End chat
          </Button>
        </div>
      ))}
    </div>
  )

  return (
    <InteractiveTooltip
      interactive
      theme="light"
      size="large"
      placement="bottom-end"
      open={menuOpen}
      setOpen={setMenuOpen}
      content={chatList}
    >
      <Button
        basic
        small
        square
        className="assist-panel__chats-menu-trigger"
        data-testid="assist-chats-menu-trigger"
        onClick={() => setMenuOpen(true)}
      >
        <IconDots />
      </Button>
    </InteractiveTooltip>
  )
}
